import React, { useContext } from 'react';
import styled from 'styled-components';
import { MdLocalShipping } from 'react-icons/md';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { SiteSettingsContext } from '@rsc/contexts/SiteSettingsContext';

import { LocalizedLink } from '@rsc/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import sharedStyles from '../styles';

type EnhancedPDPMessages = {
  shippingPolicyLink?: string;
  returnPolicyNode?: React.ReactNode;
};

type ShippingPromoProps = {
  productPrice: number;
  isSubscriptionProduct: boolean;
  isDigitalProduct: boolean;
  useEnhancedPDPMessaging?: boolean;
  enhancedPDPMessages?: EnhancedPDPMessages;
};

const StyledContainer = styled.li`
  margin: ${sharedStyles.spacing['spacing-md']} 0;
  display: flex;
  gap: ${sharedStyles.spacing['spacing-1xs']};
`;

const IconContainer = styled.div`
  color: ${({ theme }) => theme.colors.brandPrimary};
  width: 1.15rem;
  height: 1.15rem;
`;

const TextColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.15;
  p {
    color: ${({ theme }) => theme.colors.brandPrimary};
    margin: 0;
    font-weight: ${sharedStyles.fontWeight.bold};
  }
`;

const InlineTextColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.15;
  p {
    margin: 0;
  }
`;

function ShippingPromo({
  productPrice,
  isSubscriptionProduct,
  isDigitalProduct,
  useEnhancedPDPMessaging,
  enhancedPDPMessages,
}: Readonly<ShippingPromoProps>) {
  const { tt, localizePrice } = useUiComponentsInternationalContext();

  const localeContext = useContext(LocaleContext);

  const currency = localeContext?.currency || 'USD';
  const { siteSettings } = useContext(SiteSettingsContext);
  const { shipping } = siteSettings;

  const threshold = parseFloat(shipping);

  if (!threshold) return null; // display nothing if threshold is not set
  if (isDigitalProduct) return null; // display nothing if product is digital (no shipping required)

  let message;

  if (productPrice >= threshold || isSubscriptionProduct) {
    message = null;
  } else {
    const price =
      currency === 'CAD'
        ? `CA$${threshold.toString()}`
        : localizePrice(threshold, 0);
    message = tt(s => s.productCommon.onOrdersOver, {
      price,
    });
  }

  return useEnhancedPDPMessaging ? (
    <StyledContainer>
      <IconContainer>
        <MdLocalShipping size={19} />
      </IconContainer>
      <InlineTextColumn>
        {message === null ? (
          <p>
            {tt(s => s.productCommon.qualifiesForFreeShipping, {
              freeShippingLink: (
                <LocalizedLink to={enhancedPDPMessages?.shippingPolicyLink}>
                  <b>{tt(s => s.productCommon.freeShippingLink)}</b>
                </LocalizedLink>
              ),
            })}
          </p>
        ) : (
          <p>
            <LocalizedLink to={enhancedPDPMessages?.shippingPolicyLink}>
              <b>{tt(s => s.productCommon.freeShippingInCapLink)}</b>
            </LocalizedLink>{' '}
            {message && <span>{message}</span>}
          </p>
        )}
      </InlineTextColumn>
    </StyledContainer>
  ) : (
    <StyledContainer>
      <IconContainer>
        <MdLocalShipping size={28} />
      </IconContainer>
      <TextColumn>
        <p>{tt(s => s.productCommon.freeShippingInCap)}</p>
        {message && <div>{message}</div>}
      </TextColumn>
    </StyledContainer>
  );
}

export default ShippingPromo;
